import { FC, useState, useEffect, useRef } from 'react';
import { Badge, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import useDebounce from '@/hooks/useDebounce';
import { getParameters } from '../../services/parameters.ts';
import { useNavigate } from 'react-router-dom';
import LocationService from '../../domain/Location/LocationService';
import ParameterService from '../../domain/Parameter/ParameterService';
import './index.scss';
import SelectLocation from '@/basicsComponents/Selects/SelectLocation';
import { useSearchParams } from 'react-router-dom';
import SelectTypeInfrastructure from '@/basicsComponents/Selects/SelectTypeInfrastructure';
import { apiController } from '@/utils/api';
import { toFilterString, hasActiveFilters, getFiltersFromStorage, buildFilterSearchParams } from '@/utils/filterUtils';
import { getOrganizationId } from '@/utils/securityUtils';

import { Search, Toggle, DataGrid } from '@hydrodigital/design-system';
import { m } from '@/paraglide/messages';
import { useLocale } from '@/contexts/LocaleContext';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';

const locationService = new LocationService();

const parameterService = new ParameterService();

const booleanOptions: ComboboxOption[] = [
	{ value: 'true', label: 'True' },
	{ value: 'false', label: 'False' },
];

interface DataType {
	infraId: number;
	infraName: string;
	infraType: string;
	infraTypeId: number;
	location: string;
	activeParams: number;
	inactiveParams: number;
	inUse: boolean;
}

/**
 * @author
 * @function @ParametersView
 **/

const InfrastructuresView: FC = () => {
	useLocale();
	const userId = localStorage.getItem('userId') || '';
	const [searchParams, setSearchParams] = useSearchParams();
	const [inputValue, setInputValue] = useState<string>('');
	const [inUse, setInUse] = useState<any>(null);
	const [showInDashboard, setShowInDashboard] = useState<any>(null);
	const [location, setLocation] = useState<any>(null);
	const [locations, setLocations] = useState<any>(null);
	const [parameters, setParameters] = useState([]);
	const [paginationState, setPaginationState] = useState({ pageIndex: 0, pageSize: 10 });
	const [totalRows, setTotalRows] = useState(0);
	const debouncedName = useDebounce(inputValue, 500);
	const selectLocationRef = useRef<any>(null);
	const selectTypeInfrastructureRef = useRef<any>(null);
	const [selectedInfraType, setselectedInfraType] = useState<any>(null);
	const FILTER_STORAGE_KEY = 'infrastructuresViewFilters';
	const navigate = useNavigate();
	const isInitialMount = useRef(true);


	useEffect(() => {
		if (isInitialMount.current) return;
		if (paginationState.pageIndex !== 0) {
			setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
		}
	}, [debouncedName, location, selectedInfraType, inUse, showInDashboard]);

	useEffect(() => {
		if (isInitialMount.current) return;
		handleUpdateParameters();
		return () => {
			apiController.abort();
		};
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedName, location, selectedInfraType, inUse, showInDashboard]);

	// useEffect(() => {
	// 	if (
	// 		name !== null ||
	// 		inUse !== null ||
	// 		showInDashboard !== null ||
	// 		searchParams.get('locationId') ||
	// 		selectedInfraType !== null
	// 	) {
	// 		setPagination({ ...pagination, page: 1 });
	// 		handleUpdateParameters(1, pagination.pageSize);
	// 	}
	// 	return () => {
	// 		apiController.abort();
	// 	};
	// }, [name, inUse, showInDashboard, searchParams.get('locationId'), selectedInfraType]);

	useEffect(() => {
		if (searchParams.get('locationId') && selectLocationRef.current) {
			const locationParam = searchParams.get('locationId');
			selectLocationRef.current.setValue(locationParam);
		}
		if (searchParams.get('typeInfrastructure') && selectTypeInfrastructureRef.current) {
			const typeInfrastructureParam: string = searchParams.get('typeInfrastructure') || '';
			selectTypeInfrastructureRef.current.setValue(Number.parseInt(typeInfrastructureParam));
		}
	}, [searchParams]);

	useEffect(() => {
		const userId = localStorage.getItem('userId') || '';
		const resLocationByUser = locationService.getLocationsByUser(userId);
		resLocationByUser.then((value) => {
			setLocations(value);
		});
	}, []);

	useEffect(() => {
		if (isInitialMount.current) {
			return;
		}

		const filters = {
			locationId: location,
			typeInfrastructure: selectedInfraType,
			inUse,
			showInDashboard,
		};

		const hasAnyFilter = Object.values(filters).some(
			(v) => v !== null && v !== undefined && v !== '',
		);
		if (hasAnyFilter) {
		
			localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));

		
			setSearchParams(buildFilterSearchParams({
				locationId: toFilterString(location),
				typeInfrastructure: toFilterString(selectedInfraType),
				inUse: toFilterString(inUse),
				showInDashboard: toFilterString(showInDashboard),
			}));
		} else {
			
			localStorage.removeItem(FILTER_STORAGE_KEY);
			setSearchParams(new URLSearchParams());
		}
	}, [location, selectedInfraType, inUse, showInDashboard, inputValue, setSearchParams]);

	
	const clearAllFilters = () => {
		console.log('Clearing all filters');

		
		setLocation(null);
		setselectedInfraType(null);
		setInUse(null);
		setShowInDashboard(null);
		setInputValue('');

		// Clear UI components
		if (selectLocationRef.current) selectLocationRef.current.setValue(undefined);
		if (selectTypeInfrastructureRef.current)
			selectTypeInfrastructureRef.current.setValue(undefined);

		// Clear localStorage and URL params
		localStorage.removeItem(FILTER_STORAGE_KEY);

		// Force clear URL params with a new instance
		setSearchParams(new URLSearchParams());

		// Force a data refresh with no filters
		setTimeout(() => {
			getParameters(getOrganizationId(), {
				page: 1,
				size: paginationState.pageSize,
			})
				.then((response) => {
					if (response) {
						setTotalRows(response.totalRecords);
						setParameters(response.data);
					}
				})
				.catch((error) => {
					console.error(error);
				});
		}, 100);
	};

	type FilterValues = {
		locationId?: string | null;
		typeInfrastructure?: string | null;
		inUse?: string | null;
		showInDashboard?: string | null;
	};

	const FILTER_KEYS: (keyof FilterValues)[] = ['locationId', 'typeInfrastructure', 'inUse', 'showInDashboard'];

	const getFiltersFromUrl = (): FilterValues => ({
		locationId: searchParams.get('locationId'),
		typeInfrastructure: searchParams.get('typeInfrastructure'),
		inUse: searchParams.get('inUse'),
		showInDashboard: searchParams.get('showInDashboard'),
	});

	const loadFiltersFromStorage = (): FilterValues =>
		getFiltersFromStorage(FILTER_STORAGE_KEY, FILTER_KEYS);

	const applyFiltersToState = (filters: FilterValues) => {
		if (filters.locationId) setLocation(filters.locationId);
		if (filters.typeInfrastructure) setselectedInfraType(Number.parseInt(filters.typeInfrastructure));
		if (filters.inUse) setInUse(filters.inUse);
		if (filters.showInDashboard) setShowInDashboard(filters.showInDashboard);
	};

	const syncFiltersToUiRefs = (filters: FilterValues) => {
		setTimeout(() => {
			if (filters.locationId && selectLocationRef.current) {
				selectLocationRef.current.setValue(filters.locationId);
			}
			if (filters.typeInfrastructure && selectTypeInfrastructureRef.current) {
				selectTypeInfrastructureRef.current.setValue(Number.parseInt(filters.typeInfrastructure));
			}
		}, 50);
	};

	const buildApiParams = (filters: FilterValues, page: number, size: number): any => {
		const params: any = { page, size };
		if (filters.locationId) params.location = filters.locationId;
		if (filters.typeInfrastructure) params.infraTypeId = Number.parseInt(filters.typeInfrastructure);
		if (filters.inUse) params.inUse = filters.inUse;
		if (filters.showInDashboard) params.showInDashboard = filters.showInDashboard;
		return params;
	};

	const fetchAndSetParameters = (apiParams: any) => {
		return getParameters(getOrganizationId(), apiParams)
			.then((response) => {
				if (response) {
					setTotalRows(response.totalRecords);
					setParameters(response.data);
				}
			})
			.catch((error) => {
				console.error(error);
			});
	};

	// Run on initial mount to restore filters from URL or localStorage
	useEffect(() => {
		const urlFilters = getFiltersFromUrl();
		const filtersToApply = hasActiveFilters(urlFilters) ? urlFilters : loadFiltersFromStorage();

		const apiParams = hasActiveFilters(filtersToApply)
			? buildApiParams(filtersToApply, 1, paginationState.pageSize)
			: { page: 1, size: paginationState.pageSize };

		if (hasActiveFilters(filtersToApply)) {
			applyFiltersToState(filtersToApply);
			setSearchParams(buildFilterSearchParams(filtersToApply));
			localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToApply));
			syncFiltersToUiRefs(filtersToApply);
		}

		fetchAndSetParameters(apiParams).finally(() => {
			isInitialMount.current = false;
		});
	}, []);

	const syncStateFromUrlParams = (): boolean => {
		const urlFilters = getFiltersFromUrl();
		const urlName = searchParams.get('name');
		let stateChanged = false;

		if (urlFilters.inUse && inUse !== urlFilters.inUse) {
			setInUse(urlFilters.inUse);
			stateChanged = true;
		}
		if (urlFilters.showInDashboard && showInDashboard !== urlFilters.showInDashboard) {
			setShowInDashboard(urlFilters.showInDashboard);
			stateChanged = true;
		}
		if (urlName && inputValue !== urlName) {
			setInputValue(urlName);
			stateChanged = true;
		}
		if (urlFilters.locationId && location !== urlFilters.locationId) {
			setLocation(urlFilters.locationId);
			selectLocationRef.current?.setValue(urlFilters.locationId);
			stateChanged = true;
		}
		if (urlFilters.typeInfrastructure) {
			const typeId = Number.parseInt(urlFilters.typeInfrastructure);
			if (selectedInfraType !== typeId) {
				setselectedInfraType(typeId);
				selectTypeInfrastructureRef.current?.setValue(typeId);
				stateChanged = true;
			}
		}

		return stateChanged;
	};

	const persistUrlFiltersToStorage = () => {
		const urlFilters = getFiltersFromUrl();
		const filters = {
			locationId: urlFilters.locationId || location,
			typeInfrastructure: urlFilters.typeInfrastructure || selectedInfraType,
			inUse: urlFilters.inUse || inUse,
			showInDashboard: urlFilters.showInDashboard || showInDashboard,
		};
		if (hasActiveFilters(filters)) {
			localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
		}
	};

	// This effect runs whenever URL parameters change (after initial mount)
	useEffect(() => {
		if (isInitialMount.current) {
			return;
		}

		const stateChanged = syncStateFromUrlParams();

		if (Array.from(searchParams.entries()).length > 0) {
			persistUrlFiltersToStorage();
		}

		if (stateChanged) {
			setTimeout(() => handleUpdateParameters(1, paginationState.pageSize), 100);
		}
	}, [searchParams]);

	const handleUpdateParameters = async (page?: number, size?: number) => {
		const organizationId = getOrganizationId();

		// Log current state for debugging
		console.log('Current filter state:', {
			debouncedName,
			inUse,
			showInDashboard,
			location,
			selectedInfraType,
			urlParams: Object.fromEntries(searchParams.entries()),
		});

		const params: {
			page: number;
			size: number;
			name?: string | null;
			inUse?: string | null;
			showInDashboard?: string | null;
			location?: string | null;
			infraTypeId?: number | null;
		} = {
			page: page || paginationState.pageIndex + 1,
			size: size || paginationState.pageSize,
		};

		// First check component state
		if (debouncedName) params.name = debouncedName;
		if (inUse !== null && inUse !== undefined) params.inUse = inUse;
		if (showInDashboard !== null && showInDashboard !== undefined)
			params.showInDashboard = showInDashboard;

		if (location !== null && location !== undefined) {
			params.location = location;
		} else {
			const locationId = searchParams.get('locationId');
			if (locationId) params.location = locationId;
		}

		if (selectedInfraType !== null && selectedInfraType !== undefined) {
			params.infraTypeId = selectedInfraType;
		} else {
			const typeFromUrl = searchParams.get('typeInfrastructure');
			if (typeFromUrl) {
				const typeId = Number.parseInt(typeFromUrl);
				params.infraTypeId = typeId;
			}
		}

		// Double-check URL parameters as fallback
		const urlInUse = searchParams.get('inUse');
		if (!params.inUse && urlInUse) params.inUse = urlInUse;

		const urlShowInDashboard = searchParams.get('showInDashboard');
		if (!params.showInDashboard && urlShowInDashboard) params.showInDashboard = urlShowInDashboard;

		// Log final parameters being sent to API
		console.log('API parameters:', params);

		getParameters(organizationId, params)
			.then((response) => {
				if (response) {
					setTotalRows(response.totalRecords);
					setParameters(response.data);
				}
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const gridColumns = [
		{
			header: 'Asset Name',
			accessorKey: 'infraName',
			cell: ({ row }: any) => {
				const { infraName, infraId } = row.original || {};
				return (
					<span>
						{infraName} <br />
						<span style={{ color: '#023E65', fontSize: 13, fontWeight: 5 }}>ID: {infraId}</span>
					</span>
				);
			},
		},
		{
			header: 'Type',
			accessorKey: 'gisFileUploadedDTOS',
			cell: ({ row }: any) => {
				const items = row.original?.gisFileUploadedDTOS ?? null;
				// Si viene del flujo GIS, usa el arreglo como solicitaste
				if (Array.isArray(items) && items.length > 0) {
					return (
						<>
							{items.map((gis: any, index: number) => (
								<span className="table-data-gis" key={index}>
									{gis.infraTypeName.charAt(0).toUpperCase() +
										gis.infraTypeName.slice(1).toLowerCase()}
								</span>
							))}
						</>
					);
				}
				// Fallback a infraType (como estaba en AntD Table)
				const infraType = row.original?.infraType;
				const fontColor = '#232B36';
				const color = '#FFEECB';
				return infraType ? (
					<Tag color={color} style={{ color: fontColor }}>
						{infraType}
					</Tag>
				) : null;
			},
		},
		{
			header: 'Location',
			accessorKey: 'location',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Active Parameters',
			accessorKey: 'activeParams',
			cell: ({ row }: any) => (
				<span className="table-data-gis">
					<Badge status="success" className="mr-1" />
					{row.original?.activeParams} active
				</span>
			),
		},
		{
			header: 'Inactive Parameters',
			accessorKey: 'inactiveParams',
			cell: ({ row }: any) => (
				<span className="table-data-gis" style={{ color: '#C4CCD5' }}>
					<Badge status="default" className="mr-1" />
					{row.original?.inactiveParams} inactive
				</span>
			),
		},
		{
			header: (
				<div>
					<Tooltip title="For an active infrastructure, it must have an active source and parameter">
						<span>In Use</span>
					</Tooltip>
				</div>
			),
			accessorKey: 'inUse',
			cell: ({ row }: any) => {
				const record = row.original;
				return (
					<Toggle
						checkedText="ON"
						uncheckedText="OFF"
						size="small"
						checked={record.inUse}
						onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
							const checked = event.target.checked;
							try {
								if (checked) {
									await parameterService.enableInUseParameter(record);
								} else {
									await parameterService.disableInuseParameter(record);
								}
								const newData: any = [...parameters];
								const index = newData.findIndex((item: any) => record.infraId === item.infraId);
								newData[index].inUse = checked;
								if (!checked) newData[index].showInDashboard = false;
								setParameters(newData);
							} catch (error) {
								console.error(error);
							}
						}}
					/>
				);
			},
		},
		{
			header: (
				<div>
					<Tooltip title="Indicates whether the infrastructure will be displayed in the dashboad">
						<span>Show in dashboard</span>
					</Tooltip>
				</div>
			),
			accessorKey: 'showInDashboard',
			cell: ({ row }: any) => {
				const record = row.original;
				return (
					<Toggle
						checkedText="ON"
						uncheckedText="OFF"
						size="small"
						disabled={!record.inUse}
						checked={record.showInDashboard}
						onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
							const checked = event.target.checked;
							try {
								const res = await parameterService.changeShowInDashboardInfrastructure(
									record.infraId,
									checked,
									record.inUse,
								);
								if (res) {
									const newData: any = [...parameters];
									const index = newData.findIndex((item: any) => record.infraId === item.infraId);
									newData[index].showInDashboard = checked;
									setParameters(newData);
								}
							} catch (error) {
								console.error(error);
							}
						}}
					/>
				);
			},
		},
		{
			header: '',
			accessorKey: 'options',
			cell: ({ row }: any) => {
				const { activeParams, infraId } = row.original || {};
				return (
					<span
						onClick={() => {
							navigate(`parameter/${infraId}`);
						}}
						className="table-data-gis add-parameter-view-info"
						style={{ cursor: 'pointer' }}
					>
						{activeParams > 0 ? 'View info' : 'Add parameter'}
					</span>
				);
			},
		},
	];

	const columns: ColumnsType<DataType> = [
		{
			title: 'Asset Name',
			dataIndex: 'infraName',
			key: 'infraName',
			width: 200,
			render: (_, { infraName, infraId }) => {
				return (
					<span>
						{infraName} <br />
						<span style={{ color: '#023E65', fontSize: 13, fontWeight: 5 }}>ID: {infraId}</span>
					</span>
				);
			},
		},
		{
			title: 'Type',
			dataIndex: 'infraType',
			key: 'infraType',
			width: 200,
			render: (_, { infraType }) => {
				const fontColor = '#232B36';
				const color = '#FFEECB';
				// if (infraType === "IN PROGRESS") {
				//   color = "#FFEECB";
				// }
				// if (infraType === "FAILED") {
				//   color = "#FFD3D3";
				// }
				return (
					infraType && (
						<Tag color={color} style={{ color: fontColor }}>
							{infraType} <br />
						</Tag>
					)
				);
			},
		},
		{
			title: 'Location',
			dataIndex: 'location',
			key: 'location',
			width: 200,
		},
		{
			title: 'Active Parameters',
			key: 'active',
			width: 200,
			render: (_, { activeParams }) => (
				<span className="table-data-gis">
					<Badge status="success" className="mr-1" />
					{activeParams} active
				</span>
			),
		},
		{
			title: 'Inactive Parameters',
			key: 'inactive',
			width: 200,
			render: (_, { inactiveParams }) => (
				<span className="table-data-gis" style={{ color: '#C4CCD5' }}>
					<Badge status="default" className="mr-1" />
					{inactiveParams} inactive
				</span>
			),
		},
		{
			title: (
				<div>
					<Tooltip title="For an active infrastructure, it must have an active source and parameter">
						<span>In Use</span>
					</Tooltip>
				</div>
			),
			key: 'inUse',
			width: 60,
			render: (record) => (
				<Toggle
					checkedText="ON"
					uncheckedText="OFF"
					size="small"
					checked={record.inUse}
					onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
						const checked = event.target.checked;
						try {
							if (checked) {
								await parameterService.enableInUseParameter(record);
							} else {
								await parameterService.disableInuseParameter(record);
							}
							const newData: any = [...parameters];
							const index = newData.findIndex((item: any) => record.infraId === item.infraId);
							newData[index].inUse = checked;
							if (!checked) newData[index].showInDashboard = false;
							setParameters(newData);
						} catch (error) {
							console.error(error);
						}
					}}
				/>
			),
		},
		{
			title: (
				<div>
					<Tooltip title="Indicates whether the infrastructure will be displayed in the dashboad">
						<span>Show in dashboard</span>
					</Tooltip>
				</div>
			),
			key: 'showInDashboard',
			width: 60,
			render: (record) => (
				<Toggle
					checkedText="ON"
					uncheckedText="OFF"
					size="small"
					disabled={!record.inUse}
					checked={record.showInDashboard}
					onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
						const checked = event.target.checked;
						try {
							const res = await parameterService.changeShowInDashboardInfrastructure(
								record.infraId,
								checked,
								record.inUse,
							);
							if (res) {
								const newData: any = [...parameters];
								const index = newData.findIndex((item: any) => record.infraId === item.infraId);
								newData[index].showInDashboard = checked;
								setParameters(newData);
							}
						} catch (error) {
							console.error(error);
						}
					}}
				/>
			),
		},
		{
			title: '',
			width: 200,
			key: 'options',
			render: (_, { activeParams, infraId }) => (
				<span
					onClick={() => {
						navigate(`parameter/${infraId}`);
					}}
					className="table-data-gis add-parameter-view-info"
				>
					{activeParams > 0 ? 'View info' : 'Add parameter'}
				</span>
			),
		},
	];

	const handleClickSelect = (value: any) => {
		// record.location = value;
		// form.setFieldsValue({ location: value });
		// console.log("value", value);
		setLocation(value);
	};

	return (
		<div className="flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
			<div className="flex flex-col" style={{ flexShrink: 0 }}>
				<div className="text-title-page">{m.infrastructures_title()}</div>
				<div className="text-subtitle-page">{m.infrastructures_subtitle()}</div>
				<div className="mt-5 flex" style={{ position: 'relative' }}>
				<Search
					placeholder="Search"
					size="small"
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onClear={() => setInputValue('')}
					showClearButton
				/>

				<div style={{ marginLeft: 30 }}>
					{/* <Locations
            locations={locations}
            handleClickSelect={handleClickSelect}
            defaultValue={false}
          /> */}
					<SelectLocation
						ref={selectLocationRef}
						userId={userId}
						onSelect={(value: any) => {
							setLocation(value);
							setSearchParams((prev) => {
								if (value) {
									prev.set('locationId', value);
								} else {
									prev.delete('locationId');

									// Check if this was the last filter and clear everything if so
									const remainingFilters = [selectedInfraType, inUse, showInDashboard, inputValue].filter(
										(v) => v !== null && v !== undefined && v !== '',
									);

									if (remainingFilters.length === 0) {
										clearAllFilters();
									}
								}
								return prev;
							});
						}}
					/>
				</div>
				<div style={{ marginLeft: 30 }}>
					<SelectTypeInfrastructure
						ref={selectTypeInfrastructureRef}
						onSelect={(value: any) => {
							if (value) {
								setselectedInfraType(value);
								setSearchParams((prev) => {
									prev.set('typeInfrastructure', value.toString());
									return prev;
								});
							} else {
								setselectedInfraType(null);
								setSearchParams((prev) => {
									prev.delete('typeInfrastructure');

									// Check if this was the last filter and clear everything if so
									const remainingFilters = [location, inUse, showInDashboard, inputValue].filter(
										(v) => v !== null && v !== undefined && v !== '',
									);

									if (remainingFilters.length === 0) {
										clearAllFilters();
									}

									return prev;
								});
							}
						}}
					/>
				</div>

				<div style={{ marginLeft: 30, width: 140 }}>
					<Combobox
						items={booleanOptions}
						value={inUse || undefined}
						onValueChange={(value) => {
							setInUse(value);
							setSearchParams((prev) => {
								prev.set('inUse', value);
								return prev;
							});
						}}
						onClear={() => {
							setInUse(null);
							setSearchParams((prev) => {
								prev.delete('inUse');

								const remainingFilters = [
									location,
									selectedInfraType,
									showInDashboard,
									inputValue,
								].filter((v) => v !== null && v !== undefined && v !== '');

								if (remainingFilters.length === 0) {
									clearAllFilters();
								}
								return prev;
							});
						}}
						searchable={false}
						size="small"
					>
						<ComboboxInput placeholder="In use" />
						<ComboboxContent>
							<ComboboxEmpty>No options.</ComboboxEmpty>
							<ComboboxList>
								{(item) => (
									<ComboboxItem key={item.value} value={item}>
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>

				<div style={{ marginLeft: 30, width: 180 }}>
					<Combobox
						items={booleanOptions}
						value={showInDashboard || undefined}
						onValueChange={(value) => {
							setShowInDashboard(value);
							setSearchParams((prev) => {
								prev.set('showInDashboard', value);
								return prev;
							});
						}}
						onClear={() => {
							setShowInDashboard(null);
							setSearchParams((prev) => {
								prev.delete('showInDashboard');

								const remainingFilters = [location, selectedInfraType, inUse, inputValue].filter(
									(v) => v !== null && v !== undefined && v !== '',
								);

								if (remainingFilters.length === 0) {
									clearAllFilters();
								}
								return prev;
							});
						}}
						searchable={false}
						size="small"
					>
						<ComboboxInput placeholder="Show in Dashboard" />
						<ComboboxContent>
							<ComboboxEmpty>No options.</ComboboxEmpty>
							<ComboboxList>
								{(item) => (
									<ComboboxItem key={item.value} value={item}>
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
				</div>
			</div>
			<div className="mt-3">
				<DataGrid
					data={parameters}
					columns={gridColumns as any}
					stickyHeader={true}
					maxHeight="calc(100vh - 300px)"
					pagination={{
						enabled: true,
						manualPagination: true,
						rowCount: totalRows,
						defaultPageSize: 10,
						pageSizeOptions: [10, 20, 50],
					}}
					paginationState={paginationState}
					onPaginationChange={setPaginationState}
				/>
			</div>
		</div>
	);
};

export default InfrastructuresView;
